import axios, { AxiosError } from 'axios';

export const replaceString = (
  text: string,
  numberofchar: number,
  charactoreplace: string
): string => {
  return text
    .substring(0, numberofchar)
    .split('')
    .map((element) => (element = charactoreplace))
    .join('')
    .concat(text.substring(numberofchar, text.length));
};

export const calculateAge = (dob: string): number => {
  const today = new Date();
  const birthDate = new Date(dob);
  let age = today.getFullYear() - birthDate.getFullYear();
  const m = today.getMonth() - birthDate.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
};

export const validateAge = (
  age: number,
  isMinorApplication: boolean,
  isPrimaryApplciant: boolean,
  accountType: string
): string => {
  let result = '';
  if (isMinorApplication) {
    if (age < 18 && isPrimaryApplciant) {
      result = 'GuardianAgeError';
    } else if (age >= 14 && !isPrimaryApplciant) {
      result = 'MinorAgeError';
    }
  } else if (accountType === 'CC') {
    if (age < 18 && isPrimaryApplciant) {
      result = 'CCApplicantAgeError';
    } else if (age < 16 && !isPrimaryApplciant) {
      result = 'CCAdditionalCardholderAgeError';
    }
  } else if (age < 14) {
    result = 'ChildAgeError';
  }

  return result;
};

export const maskMobileNumber = (mobile: string): string => {
  return replaceString(mobile, 7, '*');
};

/**
 * Check if the input value is empty
 * @param value any String, Boolean or Object value
 * @returns {boolean} if input value is empty
 * Check empty value for: null, undefined, '', [], {}
 *
 * https://stackoverflow.com/questions/5515310/is-there-a-standard-function-to-check-for-null-undefined-or-blank-variables-in
 */
export const isEmpty = (value): boolean => {
  return (
    // null or undefined
    value == null ||
    // has length and it's zero
    (value.hasOwnProperty('length') && value.length === 0) ||
    // is an Object and has no keys
    (value.constructor === Object && Object.keys(value).length === 0)
  );
};

export const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
});

/* Get URL and return pathname as array */
export const getLocationPathArray = (): string[] => {
  let pathArray = window.location.pathname.split('/');
  if (pathArray[0] === '') {
    pathArray.shift();
  }

  return pathArray;
};

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const isMergeableObject = (val) => {
  var nonNullObject = val && typeof val === 'object';

  return (
    nonNullObject &&
    Object.prototype.toString.call(val) !== '[object RegExp]' &&
    Object.prototype.toString.call(val) !== '[object Date]'
  );
};

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const emptyTarget = (val) => {
  return Array.isArray(val) ? [] : {};
};

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function cloneIfNecessary(value, optionsArgument) {
  var clone = optionsArgument && optionsArgument.clone === true;
  return clone && isMergeableObject(value)
    ? deepMerge(emptyTarget(value), value, optionsArgument)
    : value;
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function defaultArrayMerge(target, source, optionsArgument) {
  var destination = target.slice();
  source.forEach(function (e, i) {
    if (typeof destination[i] === 'undefined') {
      destination[i] = cloneIfNecessary(e, optionsArgument);
    } else if (isMergeableObject(e)) {
      destination[i] = deepMerge(target[i], e, optionsArgument);
    } else if (target.indexOf(e) === -1) {
      destination.push(cloneIfNecessary(e, optionsArgument));
    }
  });
  return destination;
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function mergeObject(target, source, optionsArgument?) {
  var destination = {};
  if (isMergeableObject(target)) {
    Object.keys(target).forEach(function (key) {
      destination[key] = cloneIfNecessary(target[key], optionsArgument);
    });
  }
  Object.keys(source).forEach(function (key) {
    if (!isMergeableObject(source[key]) || !target[key]) {
      destination[key] = cloneIfNecessary(source[key], optionsArgument);
    } else {
      destination[key] = deepMerge(target[key], source[key], optionsArgument);
    }
  });
  return destination;
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function deepMerge(target, source, optionsArgument?) {
  var array = Array.isArray(source);
  var options = optionsArgument || { arrayMerge: defaultArrayMerge };
  var arrayMerge = options.arrayMerge || defaultArrayMerge;

  if (array) {
    return Array.isArray(target)
      ? arrayMerge(target, source, optionsArgument)
      : cloneIfNecessary(source, optionsArgument);
  } else {
    return mergeObject(target, source, optionsArgument);
  }
}

/* Error handler for createAsyncThunk */
export const thunkHandler = async (
  asyncPromise,
  rejectWithValue
): Promise<any> => {
  try {
    const response = await asyncPromise;

    return response.data;
  } catch (err) {
    if (axios.isAxiosError(err)) {
      let error: AxiosError<string> = err; // cast the error for access
      if (!error.response) {
        throw err;
      }
      return rejectWithValue(error.response.data) as { status: number };
    } else {
      throw err;
    }
  }
};
